import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import SignaturePad from 'signature_pad';

export interface SignatureCaptureDialogProps {
  open: boolean;
  title?: string;
  initialSignature?: string | null;
  onCancel: () => void;
  onSave: (signatureBase64Png: string) => void;
}

const SIGNATURE_CANVAS_HEIGHT_PX = 220;
const EPAD_UNAVAILABLE_MESSAGE =
  'ePad extension not detected. Install and enable the ePad browser extension, then reopen this dialog to capture from ePad.';
const EPAD_SIGCAPTUREWEB_START_EVENT = 'SigCaptureWeb_SignStartEvent';
const EPAD_SIGCAPTUREWEB_RESPONSE_EVENT = 'SigCaptureWeb_SignResponse';
const EPAD_SIGCAPTUREWEB_ELEMENT_TAG = 'SigCaptureWeb_ExtnDataElem';
const EPAD_SIGCAPTUREWEB_MSG_ATTR = 'SigCaptureWeb_MsgAttribute';
const EPAD_SIGCAPTUREWEB_RESPONSE_ATTR = 'SigCaptureWeb_msgAttri';
/** Timeout (ms) waiting for the user to sign on the ePad device. */
const EPAD_CAPTURE_TIMEOUT_MS = 30_000;
const EPAD_CAPTURE_METHODS = [
  'captureSignature',
  'capture',
  'requestSignature',
  'startCapture',
  'getSignature',
] as const;
const EPAD_GLOBAL_KEYS = [
  'ePad',
  'epad',
  'ePadExtension',
  'EPadExtension',
  'ePadInk',
  'EPadInk',
  'ePadApi',
  'EPadApi',
] as const;
const EPAD_RESULT_STRING_KEYS = [
  'dataUrl',
  'dataURL',
  'imageDataUrl',
  'imageDataURL',
  'signatureDataUrl',
  'signatureDataURL',
  'signatureBase64',
  'imageBase64',
  'base64',
  'signature',
] as const;
const EPAD_RESULT_CONTAINER_KEYS = ['result', 'data', 'payload', 'signature'] as const;

type EPadCaptureApi = {
  captureSignature: () => Promise<unknown> | unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
};

const asFunction = (
  container: Record<string, unknown>,
  key: string,
): ((...args: unknown[]) => unknown) | null => {
  const fn = container[key];
  return typeof fn === 'function' ? (fn as (...args: unknown[]) => unknown) : null;
};

const resolveEPadResultString = (result: unknown): string | null => {
  if (typeof result === 'string' && result.trim().length > 0) {
    return result.trim();
  }

  const resultRecord = asRecord(result);
  if (!resultRecord) {
    return null;
  }

  for (const key of EPAD_RESULT_STRING_KEYS) {
    const candidate = resultRecord[key];
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }

  for (const key of EPAD_RESULT_CONTAINER_KEYS) {
    const nestedRecord = asRecord(resultRecord[key]);
    if (!nestedRecord) {
      continue;
    }

    for (const nestedKey of EPAD_RESULT_STRING_KEYS) {
      const candidate = nestedRecord[nestedKey];
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
  }

  return null;
};

const normalizeSignatureDataUrl = (signatureValue: string): string => {
  if (/^data:image\//i.test(signatureValue)) {
    return signatureValue;
  }
  return `data:image/png;base64,${signatureValue}`;
};

/**
 * Build an EPadCaptureApi that communicates with the ePadLink SigCaptureWeb
 * browser extension via DOM custom events (the same protocol used by the
 * ePadLink test page at c3.autoslm.net/sandbox/signature).
 *
 * This works even when the extension injects no window globals, which is why
 * window-object-only detection misses it.
 */
const createSigCaptureWebApi = (): EPadCaptureApi => ({
  captureSignature: (): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      let requestElement: Element | null = null;

      const removeResponseListener = () => {
        document.removeEventListener(EPAD_SIGCAPTUREWEB_RESPONSE_EVENT, handleResponse, true);
      };

      const removeRequestElement = () => {
        if (requestElement?.parentNode) {
          requestElement.parentNode.removeChild(requestElement);
        }
      };

      const handleResponse = (event: Event) => {
        clearTimeout(timeoutId);
        removeResponseListener();
        removeRequestElement();
        const target = event.target as Element | null;
        const responseStr = target?.getAttribute(EPAD_SIGCAPTUREWEB_RESPONSE_ATTR);
        if (!responseStr) {
          reject(new Error('No response data returned from ePad.'));
          return;
        }
        try {
          const response = JSON.parse(responseStr) as Record<string, unknown>;
          const errorMsg =
            typeof response.errorMsg === 'string' &&
            response.errorMsg.trim() !== '' &&
            response.errorMsg.trim() !== 'undefined'
              ? response.errorMsg.trim()
              : null;
          if (errorMsg) {
            reject(new Error(errorMsg));
            return;
          }
          if (!response.isSigned) {
            reject(new Error('Signature was not captured on the ePad device.'));
            return;
          }
          // SDK Guide v1.2 names the field 'imgData'; the legacy reference page
          // and some older SDK builds use 'imageData'. Accept either.
          const imageData = response.imgData ?? response.imageData;
          if (typeof imageData === 'string' && imageData.trim().length > 0) {
            resolve(imageData.trim());
          } else {
            reject(new Error('No signature image data was returned from the ePad device.'));
          }
        } catch {
          reject(new Error('Failed to parse the ePad device response.'));
        }
      };

      document.addEventListener(EPAD_SIGCAPTUREWEB_RESPONSE_EVENT, handleResponse, true);

      const timeoutId = setTimeout(() => {
        removeResponseListener();
        removeRequestElement();
        reject(
          new Error(
            'The ePad device did not respond. Ensure the ePadLink SigCaptureWeb extension is installed and the device is connected.',
          ),
        );
      }, EPAD_CAPTURE_TIMEOUT_MS);

      const message = {
        firstName: '',
        lastName: '',
        eMail: '',
        location: '',
        // imageFormat: 1 = JPG, 2 = PNG (per SDK Guide v1.2 §9.1.1)
        imageFormat: 2,
        // Higher resolution → sharper exported image. The pad renders at its
        // native digitizer resolution internally; imageX/Y only affect the
        // PNG that gets base64-encoded and returned to us.
        imageX: 1200,
        imageY: 400,
        imageTransparency: false,
        imageScaling: false,
        maxUpScalePercent: 0.0,
        rawDataFormat: 'ENC',
        minSigPoints: 25,
        // penThickness 1–7 recommended (SDK Guide v1.2 §9.1.1); affects both
        // on-screen preview in the SDK popup AND the exported image.
        penThickness: 2,
        penColor: '#000000',
      };

      requestElement = document.createElement(EPAD_SIGCAPTUREWEB_ELEMENT_TAG);
      requestElement.setAttribute(EPAD_SIGCAPTUREWEB_MSG_ATTR, JSON.stringify(message));
      document.documentElement.appendChild(requestElement);

      const evt = document.createEvent('Events');
      evt.initEvent(EPAD_SIGCAPTUREWEB_START_EVENT, true, false);
      requestElement.dispatchEvent(evt);
    }),
});

const detectEPadCaptureApi = (): EPadCaptureApi | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Official SDK detection method (SDK Guide v1.2 §6.1): the extension sets
  // this attribute on <html> at startup. If present, route directly to the
  // SigCaptureWeb DOM-event API rather than scanning window globals.
  if (
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('SigCaptureWebExtension-installed') !== null
  ) {
    return createSigCaptureWebApi();
  }

  const windowRecord = window as unknown as Record<string, unknown>;

  for (const globalKey of EPAD_GLOBAL_KEYS) {
    const runtime = asRecord(windowRecord[globalKey]);
    if (!runtime) {
      continue;
    }

    const candidates: Record<string, unknown>[] = [runtime];
    const nestedApi = asRecord(runtime.api);
    if (nestedApi) {
      candidates.push(nestedApi);
    }
    const nestedExtension = asRecord(runtime.extension);
    if (nestedExtension) {
      candidates.push(nestedExtension);
    }

    for (const candidate of candidates) {
      const availableCheck = asFunction(candidate, 'isAvailable');
      if (availableCheck) {
        try {
          const isAvailable = availableCheck.call(candidate);
          if (typeof isAvailable === 'boolean' && !isAvailable) {
            continue;
          }
        } catch {
          continue;
        }
      }

      for (const methodName of EPAD_CAPTURE_METHODS) {
        const captureMethod = asFunction(candidate, methodName);
        if (!captureMethod) {
          continue;
        }
        return {
          captureSignature: () => captureMethod.call(candidate),
        };
      }
    }
  }

  // No window-global API found — fall back to the ePadLink SigCaptureWeb
  // DOM-event protocol. The extension communicates entirely via custom DOM
  // events and injects no window globals, so window-object scanning alone
  // will always miss it. We always return an API here; if the extension is
  // genuinely absent the capture call will time out with a clear error.
  return createSigCaptureWebApi();
};

/**
 * Returns true only when the ePad browser extension is positively detected —
 * either via the official SDK attribute on <html> or via a window global.
 * Unlike detectEPadCaptureApi, this never returns a speculative fallback.
 */
const isEPadExtensionPresent = (): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  // Official SDK detection (SDK Guide v1.2 §6.1).
  if (document.documentElement.getAttribute('SigCaptureWebExtension-installed') !== null) {
    return true;
  }

  const windowRecord = window as unknown as Record<string, unknown>;
  for (const globalKey of EPAD_GLOBAL_KEYS) {
    if (asRecord(windowRecord[globalKey])) {
      return true;
    }
  }

  return false;
};

export const SignatureCaptureDialog = ({
  open,
  title = 'Capture Signature',
  initialSignature,
  onCancel,
  onSave,
}: SignatureCaptureDialogProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const ePadCaptureApiRef = useRef<EPadCaptureApi | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [isPadReady, setIsPadReady] = useState(false);
  const [isEPadAvailable, setIsEPadAvailable] = useState(false);
  const [isCapturingWithEPad, setIsCapturingWithEPad] = useState(false);
  const [ePadCaptureError, setEPadCaptureError] = useState<string | null>(null);

  const initialSignatureDataUrl = useMemo(() => {
    if (!initialSignature?.trim()) {
      return null;
    }
    return `data:image/png;base64,${initialSignature.trim()}`;
  }, [initialSignature]);

  const syncHasSignature = useCallback(() => {
    const pad = signaturePadRef.current;
    if (!pad) {
      setHasSignature(false);
      return;
    }
    setHasSignature(!pad.isEmpty());
  }, []);

  const resizeCanvas = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);

    const pad = signaturePadRef.current;
    const data = pad ? pad.toData() : [];

    canvas.width = width * ratio;
    canvas.height = height * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    if (pad) {
      pad.clear();
      if (data.length > 0) {
        pad.fromData(data);
      }
    }
  }, []);

  const initializePad = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    if (signaturePadRef.current) {
      signaturePadRef.current.removeEventListener('endStroke', syncHasSignature);
      signaturePadRef.current.off();
    }
    signaturePadRef.current = null;

    resizeCanvas();

    const pad = new SignaturePad(canvas, {
      penColor: 'rgba(15,23,42,0.85)',
      minWidth: 0.8,
      maxWidth: 2.5,
      // 0 = capture every pointer event; gives the Bézier spline the maximum
      // number of control points so curves are smooth instead of angular.
      throttle: 0,
      // Higher weight means more of the previous velocity is carried forward,
      // which damps sudden direction changes and reduces shakiness.
      velocityFilterWeight: 0.8,
    });
    signaturePadRef.current = pad;

    pad.addEventListener('endStroke', syncHasSignature);

    if (initialSignatureDataUrl) {
      try {
        pad.fromDataURL(initialSignatureDataUrl);
      } catch {
        // Ignore invalid signature data; allow user to capture a new one.
      }
    }

    syncHasSignature();
    setIsPadReady(true);
  }, [initialSignatureDataUrl, resizeCanvas, syncHasSignature]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [open, resizeCanvas]);

  useEffect(() => {
    if (!open) {
      ePadCaptureApiRef.current = null;
      setIsEPadAvailable(false);
      setIsCapturingWithEPad(false);
      setEPadCaptureError(null);
      return;
    }

    const detectedApi = detectEPadCaptureApi();
    ePadCaptureApiRef.current = detectedApi;
    setIsEPadAvailable(isEPadExtensionPresent());
    setIsCapturingWithEPad(false);
    setEPadCaptureError(null);
  }, [open]);

  useEffect(() => {
    if (!open) {
      if (signaturePadRef.current) {
        signaturePadRef.current.removeEventListener('endStroke', syncHasSignature);
        signaturePadRef.current.off();
      }
      signaturePadRef.current = null;
      setHasSignature(false);
      setIsPadReady(false);
    }
  }, [open, syncHasSignature]);

  const handleClear = () => {
    signaturePadRef.current?.clear();
    setHasSignature(false);
  };

  const handleCaptureWithEPad = async () => {
    const captureApi = ePadCaptureApiRef.current;
    const pad = signaturePadRef.current;
    if (!captureApi || !pad) {
      return;
    }

    setIsCapturingWithEPad(true);
    setEPadCaptureError(null);

    try {
      const captureResult = await Promise.resolve(captureApi.captureSignature());
      const signatureResult = resolveEPadResultString(captureResult);
      if (!signatureResult) {
        throw new Error('No signature image data was returned.');
      }

      pad.fromDataURL(normalizeSignatureDataUrl(signatureResult));
      syncHasSignature();
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : 'Please ensure the ePad extension is enabled and try again.';
      setEPadCaptureError(`Unable to capture from ePad. ${message}`);
    } finally {
      setIsCapturingWithEPad(false);
    }
  };

  const handleSave = () => {
    const pad = signaturePadRef.current;
    if (!pad || pad.isEmpty()) {
      setHasSignature(false);
      return;
    }

    const dataUrl = pad.toDataURL('image/png');
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    onSave(base64);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      TransitionProps={{
        onEntered: initializePad,
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {!isEPadAvailable ? (
            <Alert severity="warning">{EPAD_UNAVAILABLE_MESSAGE}</Alert>
          ) : null}
          {ePadCaptureError ? <Alert severity="error">{ePadCaptureError}</Alert> : null}
          <Box
            ref={containerRef}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#ffffff',
              height: SIGNATURE_CANVAS_HEIGHT_PX,
              overflow: 'hidden',
            }}
          >
            <Box
              component="canvas"
              ref={canvasRef}
              sx={{
                width: '100%',
                height: '100%',
                display: 'block',
                touchAction: 'none',
                // Softens hard pixel edges of the strokes during display.
                filter: 'blur(0.4px)',
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => {
            void handleCaptureWithEPad();
          }}
          color="inherit"
          disabled={!isEPadAvailable || !isPadReady || isCapturingWithEPad}
        >
          {isCapturingWithEPad ? 'Capturing with ePad...' : 'Capture with ePad'}
        </Button>
        <Button onClick={handleClear} disabled={!hasSignature} color="inherit">
          Clear
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasSignature}>
          Save Signature
        </Button>
      </DialogActions>
    </Dialog>
  );
};
